import {
  Alert,
  Box,
  Button,
  Container,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoctorService from "../../services/doctorService";

export default function DoctorForgotPassword({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const doctorService = new DoctorService();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!email || !email.trim()) {
      setError("Please enter your email address");
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    try {
      setLoading(true);
      await doctorService.requestPasswordOtp(email);
      
      setSnackbar({
        open: true,
        message: "Verification code sent to your email!",
        severity: "success",
      });
      
      // 2 saniye sonra OTP doğrulama sayfasına yönlendir
      setTimeout(() => {
        navigate("/verify-otp/doctor", { state: { email } });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "An error occurred while sending verification code");
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/individual-login")}
                sx={{
                  mb: 2,
                  color: darkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.95)",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  },
                }}
              >
                Back to Login
              </Button>

              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]}, ${colors.greenAccent[400]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Forgot Password
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  mb: 4,
                  color: darkMode ? colors.grey[300] : colors.grey[600],
                }}
              >
                Enter your email address and we'll send you a verification code
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    value={email}
                    placeholder="example@email.com"
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: "white",
                    color: colors.greenAccent[600],
                    borderRadius: "12px",
                    padding: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.95)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                    "&:disabled": {
                      background: colors.grey[500],
                      color: colors.grey[300],
                    },
                  }}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                </Button>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
            }}
          >
            <Typography variant="body1" color="white">
              {snackbar.message}
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}

